import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Icon } from '@iconify/react'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import Card from '../components/common/Card'
import PageContainer from '../components/layout/PageContainer'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Select from '../components/common/Select'
import Dialog from '../components/common/Dialog'
import ConfirmDialog from '../components/common/ConfirmDialog'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { toast } from 'react-toastify'
import { teamService } from '../services/api/teamService'
import { combinePhoneNumber, parsePhoneNumberToParts } from '../utils/phoneHelpers'

const Team = () => {
  const [showMemberDialog, setShowMemberDialog] = useState(false)
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [editingMember, setEditingMember] = useState(null)
  const [memberToRemove, setMemberToRemove] = useState(null)
  const [members, setMembers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      contact: '',
      status: 'active',
    },
  })
  const contactValue = watch('contact')

  const getApiMessage = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback

  const getFieldErrorMessage = (error, field) =>
    error?.response?.data?.errors?.[field]

  const getStatusClass = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700'
      case 'inactive':
        return 'bg-gray-100 text-gray-600'
      case 'blocked':
        return 'bg-red-100 text-red-700'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'deleted':
        return 'bg-slate-100 text-slate-600'
      default:
        return 'bg-blue-100 text-blue-700'
    }
  }

  const getAvatarText = (member) => {
    const first = member?.firstName?.[0] || ''
    const last = member?.lastName?.[0] || ''
    return `${first}${last}`.toUpperCase() || 'TM'
  }

  const fetchMembers = async (page = currentPage) => {
    try {
      setIsLoading(true)
      const response = await teamService.list({
        search,
        status: statusFilter,
        page,
        limit: 10,
        sortBy: 'createdAt:desc',
      })
      const results = response?.results || []
      setMembers(results)
      setPagination({
        page: response?.page || page,
        limit: response?.limit || 10,
        totalPages: response?.totalPages || 1,
        totalResults: response?.totalResults || results.length,
      })
      setCurrentPage(response?.page || page)
    } catch (error) {
      toast.error(getApiMessage(error, 'Failed to load team members'))
      setMembers([])
      setPagination((prev) => ({ ...prev, totalResults: 0 }))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchMembers(currentPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, currentPage])

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = searchInput.trim()
      setSearch((prev) => (prev === nextSearch ? prev : nextSearch))
      setCurrentPage(1)
    }, 400)

    return () => clearTimeout(timer)
  }, [searchInput])

  const handleInviteClick = () => {
    setEditingMember(null)
    reset({
      firstName: '',
      lastName: '',
      email: '',
      contact: '',
      status: 'active',
    })
    setShowMemberDialog(true)
  }

  const handleEditClick = async (member) => {
    try {
      setIsSubmitting(true)
      const memberId = member._id || member.id
      const detailedMember = await teamService.getById(memberId)
      setEditingMember({ ...member, ...detailedMember })
      setValue('firstName', detailedMember.firstName || '')
      setValue('lastName', detailedMember.lastName || '')
      setValue('email', detailedMember.email || '')
      setValue(
        'contact',
        combinePhoneNumber(detailedMember.countryCode, detailedMember.contact),
      )
      setValue('status', detailedMember.status || 'active')
      setShowMemberDialog(true)
    } catch (error) {
      toast.error(getApiMessage(error, 'Failed to load team member details'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveClick = (member) => {
    setMemberToRemove(member)
    setShowRemoveDialog(true)
  }

  const onSubmitMember = async (data) => {
    try {
      setIsSubmitting(true)
      const { countryCode, contact } = parsePhoneNumberToParts(data.contact)

      if (!contact) {
        setError('contact', { type: 'manual', message: 'Contact number is required' })
        setIsSubmitting(false)
        return
      }

      clearErrors('contact')

      if (editingMember) {
        const payload = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          status: data.status,
        }

        payload.contact = contact
        payload.countryCode = countryCode

        const memberId = editingMember._id || editingMember.id
        await teamService.update(memberId, payload)
        toast.success(`Member ${data.email} updated successfully`)
      } else {
        await teamService.create({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          countryCode,
          contact,
        })
        toast.success(`Invitation sent to ${data.email}`)
      }
      await fetchMembers(1)
      setCurrentPage(1)
      reset({
        firstName: '',
        lastName: '',
        email: '',
        contact: '',
        status: 'active',
      })
      setShowMemberDialog(false)
      setEditingMember(null)
    } catch (error) {
      const emailError = getFieldErrorMessage(error, 'email')
      const contactError = getFieldErrorMessage(error, 'contact')
      toast.error(emailError || contactError || getApiMessage(error, 'Failed to save team member'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveConfirm = async () => {
    if (!memberToRemove) return

    try {
      setIsDeleting(true)
      const memberId = memberToRemove._id || memberToRemove.id
      await teamService.remove(memberId)
      toast.success(`${memberToRemove.fullName || `${memberToRemove.firstName} ${memberToRemove.lastName}`} removed successfully`)
      setMemberToRemove(null)
      await fetchMembers(currentPage)
    } catch (error) {
      toast.error(getApiMessage(error, 'Failed to remove member'))
    } finally {
      setIsDeleting(false)
    }
  }

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'blocked', label: 'Blocked' },
  ]

  const listStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'deleted', label: 'Deleted' },
  ]

  return (
    <PageContainer title="Team Management" subtitle="Manage your team members and permissions">
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between mb-4 sm:mb-6 gap-3">
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <Input
            placeholder="Search by name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="mb-0"
          />
          <Select
            options={listStatusOptions}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setCurrentPage(1)
            }}
            className="mb-0 min-w-[180px]"
          />
        </div>
        <Button
          icon="mdi:account-plus"
          onClick={handleInviteClick}
          className="w-full lg:w-auto"
          size="sm"
        >
          <span className="hidden xs:inline">Invite Member</span>
          <span className="xs:hidden">Invite</span>
        </Button>
      </div>

      <Card title="Team Members" subtitle={`${pagination.totalResults} total members`}>
        <div className="space-y-3 sm:space-y-4">
          {isLoading ? (
            <div className="py-12">
              <LoadingSpinner />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-10 text-text-secondary">
              No team members found.
            </div>
          ) : (
            members.map((member) => (
              <div
                key={member._id || member.id}
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-bg-light rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white font-semibold text-sm sm:text-base flex-shrink-0">
                    {getAvatarText(member)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base text-text-dark mb-0.5 sm:mb-1 truncate">
                      {member.fullName || `${member.firstName || ''} ${member.lastName || ''}`.trim()}
                    </h3>
                    <p className="text-xs sm:text-sm text-text-secondary mb-1 sm:mb-1 truncate">{member.email}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${getStatusClass(member.status)}`}>
                        {(member.status || 'active').toUpperCase()}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {member.contact || 'No contact'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="hidden md:flex items-center gap-4 sm:gap-6 flex-shrink-0">
                  <div className="text-right flex gap-2">
                    <div className="text-xs sm:text-sm font-semibold text-text-dark whitespace-nowrap">
                      Team Member
                    </div>
                    <div className="text-xs text-text-secondary">•</div>
                    <div className="text-xs sm:text-sm font-semibold text-text-dark whitespace-nowrap">
                      {member.isActive ? 'Can Login' : 'Blocked Login'}
                    </div>
                  </div>
                </div>

                <div className="flex md:hidden items-center justify-between px-2 py-1 bg-white/50 rounded text-xs">
                  <span className="text-text-secondary">Role:</span>
                  <span className="font-semibold text-text-dark">Team Member</span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="mdi:pencil"
                    className="hidden sm:flex"
                    onClick={() => handleEditClick(member)}
                    disabled={isSubmitting}
                  >
                    <span className="hidden md:inline">Edit</span>
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="mdi:delete"
                    className="hidden sm:flex"
                    onClick={() => handleRemoveClick(member)}
                    disabled={isDeleting}
                  >
                    <span className="hidden md:inline">Remove</span>
                  </Button>

                  <div className="flex sm:hidden items-center gap-1.5">
                    <button
                      onClick={() => handleEditClick(member)}
                      className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      aria-label="Edit member"
                    >
                      <Icon icon="mdi:pencil" className="text-lg" />
                    </button>
                    <button
                      onClick={() => handleRemoveClick(member)}
                      className="p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors"
                      aria-label="Remove member"
                    >
                      <Icon icon="mdi:delete" className="text-lg" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {pagination.totalPages > 1 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || isLoading}
            >
              Prev
            </Button>
            <p className="text-sm text-text-secondary">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(pagination.totalPages, prev + 1))}
              disabled={currentPage === pagination.totalPages || isLoading}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
      {/* Invite/Edit Member Dialog */}
      <Dialog
        isOpen={showMemberDialog}
        onClose={() => {
          setShowMemberDialog(false)
          setEditingMember(null)
          reset()
        }}
        title={editingMember ? 'Edit Team Member' : 'Invite Team Member'}
        size="md"
        footer={
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 w-full">
            <Button
              variant="secondary"
              onClick={() => {
                setShowMemberDialog(false)
                setEditingMember(null)
                reset()
              }}
              className="w-full sm:w-auto"
              size="sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="member-form"
              icon={editingMember ? "mdi:content-save" : "mdi:send"}
              className="w-full sm:w-auto"
              size="sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Please wait...' : editingMember ? 'Save Changes' : 'Send Invitation'}
            </Button>
          </div>
        }
      >
        <form id="member-form" onSubmit={handleSubmit(onSubmitMember)} className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="First Name"
              placeholder="Jane"
              error={errors.firstName?.message}
              {...register('firstName', { required: 'First name is required' })}
            />
            <Input
              label="Last Name"
              placeholder="Doe"
              error={errors.lastName?.message}
              {...register('lastName', { required: 'Last name is required' })}
            />
          </div>
          <Input
            label="Email Address"
            type="email"
            placeholder="colleague@example.com"
            error={errors.email?.message}
            {...register('email', { required: 'Email is required' })}
          />
          <div className="space-y-2">
            <label className="form-label">Contact Number</label>
            <div className="relative border border-border-light rounded-lg p-2">
              <PhoneInput
                international
                defaultCountry="PK"
                value={contactValue || ''}
                onChange={(value) => {
                  setValue('contact', value || '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
                onBlur={() => {
                  setValue('contact', getValues('contact'), {
                    shouldValidate: true,
                  })
                }}
                placeholder="Enter phone number"
                className="w-full"
              />
            </div>
            {errors.contact && <p className="mt-1 text-sm text-danger">{errors.contact.message}</p>}
          </div>
          {editingMember && (
            <Select
              label="Status"
              options={statusOptions}
              error={errors.status?.message}
              {...register('status', { required: 'Status is required' })}
              value={watch('status')}
            />
          )}
        </form>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRemoveDialog}
        onClose={() => {
          setShowRemoveDialog(false)
          setMemberToRemove(null)
        }}
        onConfirm={handleRemoveConfirm}
        title="Remove Team Member"
        message={
          memberToRemove
            ? `Are you sure you want to remove ${memberToRemove.fullName || `${memberToRemove.firstName || ''} ${memberToRemove.lastName || ''}`.trim() || memberToRemove.email} from the team? This action cannot be undone.`
            : 'Are you sure you want to remove this team member?'
        }
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        icon="mdi:account-remove"
      />
    </PageContainer>
  )
}

export default Team
